import { useState, useRef, useEffect } from "react";
import "./App.css";
import video from "./assets/web_background.webm";
import logo from "./assets/gehc_logo_big.png";
import puz1 from "./assets/jigsaw_01-optimized.gif";
import puz2 from "./assets/jigsaw_02-optimized.gif";
import puz3 from "./assets/jigsaw_03-optimized.gif";
import puz4 from "./assets/jigsaw_04-optimized.gif";
import arrow from "./assets/arrow_01.png";

function App() {
  const saws = [puz1, puz2, puz3, puz4];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSwapped, setIsSwapped] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Touch/mouse tracking
  const startY = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);
  const swapElementRef = useRef(null);

  // Add passive event listeners manually
  useEffect(() => {
    const element = swapElementRef.current;
    if (!element) return;

    const handleTouchMovePassive = (e) => {
      if (!isDragging.current || isSwapped || isAnimating) return;
      currentY.current = e.touches[0].clientY;

      // Calculate drag distance and apply real-time transform
      const deltaY = startY.current - currentY.current;
      if (deltaY > 0 && swapElementRef.current) {
        const translateY = Math.min(deltaY * 0.5, 100); // Limit max preview movement
        swapElementRef.current.style.transform = `translateY(-${translateY}px)`;
      }

      // Prevent default to avoid scrolling while swiping
      e.preventDefault();
    };

    // Add event listener with passive: false
    element.addEventListener("touchmove", handleTouchMovePassive, { passive: false });

    return () => {
      element.removeEventListener("touchmove", handleTouchMovePassive);
    };
  }, [isSwapped, isAnimating]);

  const handleSwapUp = () => {
    setIsAnimating(true);
    setIsSwapped(true);

    // Add transform animation immediately
    if (swapElementRef.current) {
      swapElementRef.current.style.transform = "translateY(-100vh)";
      swapElementRef.current.style.opacity = "0";
    }

    setTimeout(() => {
      setIsModalOpen(true);
      setIsAnimating(false);
    }, 800); // Increased delay to show full animation
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      setIsSwapped(false);
      // Reset transform
      if (swapElementRef.current) {
        swapElementRef.current.style.transform = "translateY(0)";
        swapElementRef.current.style.opacity = "1";
      }
    }, 200);
  };

  // Touch events for mobile
  const handleTouchStart = (e) => {
    if (isSwapped || isAnimating) return;
    startY.current = e.touches[0].clientY;
    currentY.current = e.touches[0].clientY;
    isDragging.current = true;
  };

  const handleTouchEnd = (e) => {
    if (!isDragging.current || isSwapped || isAnimating) return;

    const deltaY = startY.current - currentY.current;
    const threshold = 150; // Minimum distance for swipe

    // Only trigger if there was actual movement (swipe), not just tap
    if (deltaY > threshold) {
      // Swipe up detected
      handleSwapUp();
      e.preventDefault(); // Prevent any click events
    } else {
      // Reset position if not enough swipe distance
      if (swapElementRef.current) {
        swapElementRef.current.style.transform = "translateY(0)";
      }
    }

    isDragging.current = false;
  };

  // Mouse events for desktop
  const handleMouseDown = (e) => {
    if (isSwapped || isAnimating) return;
    startY.current = e.clientY;
    currentY.current = e.clientY;
    isDragging.current = true;
    // Prevent text selection
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current || isSwapped || isAnimating) return;
    currentY.current = e.clientY;

    // Calculate drag distance and apply real-time transform
    const deltaY = startY.current - currentY.current;
    if (deltaY > 0 && swapElementRef.current) {
      const translateY = Math.min(deltaY * 0.3, 80); // Limit max preview movement for desktop
      swapElementRef.current.style.transform = `translateY(-${translateY}px)`;
    }
  };

  const handleMouseUp = (e) => {
    if (!isDragging.current || isSwapped || isAnimating) return;

    const deltaY = startY.current - currentY.current;
    const threshold = 150; // Minimum distance for swipe on desktop

    // Only trigger if there was actual movement (drag), not just click
    if (deltaY > threshold) {
      // Swipe up detected
      handleSwapUp();
      e.preventDefault(); // Prevent any click events
    } else {
      // Reset position if not enough drag distance
      if (swapElementRef.current) {
        swapElementRef.current.style.transform = "translateY(0)";
      }
    }

    isDragging.current = false;
  };

  // Prevent click events when we've handled swipe
  const handleClick = (e) => {
    if (isAnimating) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div className="App">
      <div className="videoBg">
        <video autoPlay loop muted playsInline>
          <source src={video} type="video/webm" />
          Your browser does not support the video tag.
        </video>
      </div>
      <div className="logo">
        <img src={logo} alt="GEHC Logo" />
      </div>
      <div
        ref={swapElementRef}
        className={`swap ${isSwapped ? "swapped-up" : ""} ${isAnimating ? "animating" : ""}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp} // Handle case when mouse leaves element
        onClick={handleClick} // Prevent accidental clicks
        style={{
          transition: isAnimating
            ? "transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.8s ease-out"
            : "transform 0.2s ease-out",
        }}
      >
        <div>
          <img src={arrow} alt="Arrow" className="bouncing-arrow" />
          <img src={saws[Math.floor(Math.random() * saws.length)]} alt="Puzzle Piece" className="puzzle-piece" />
          <h2>
            Swipe Up <br />
            The Puzzle Piece
          </h2>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              Thank you
              <br />
              for your support in this <br />
              launcing session!
            </h3>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
